import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { useErrorInputs } from '../../../hooks/useErrorInputs';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { getUsers, getRoles, getWareHouses } from '../../../services/order.service';
import moment from 'moment';

export const OrderForm = forwardRef(({ dto, handleInputChange, handleDirectInputChange }, ref) => {
    const fieldsRequired = ['currency', 'approver', 'group', 'id_warehouse'];
    let validationApprover_Group = (dto.id_group && !dto.id_approver) || (!dto.id_group && dto.id_approver);
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();
    const [errorInputs, , , validForm] = useErrorInputs({});
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [wareHouses, setWareHouses] = useState([]);
    let idCompany =  dto.id_company;
    
    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto, validationApprover_Group)
    }));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [usersResponse, rolesResponse, wareHousesResponse] = await Promise.all([
                    getUsers(userSession.token, idCompany),
                    getRoles(userSession.token, idCompany),
                    getWareHouses(userSession.token, dto.id_company)
                ]);
                setUsers(usersResponse.data || []);
                setRoles(rolesResponse.data || []);
                setWareHouses(wareHousesResponse.data || []);
            } catch (err) {
                console.error(err.message);
            }
        };

        fetchData();
    }, [idCompany, userSession.token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'id_approver') {
            const { user_name, id_user } = users.find(user => user.id_user === value);
            handleDirectInputChange('approver', user_name);
            handleDirectInputChange('id_approver', id_user);
        } else if (name === 'id_group') {
            const { id_user_group, name } = roles.find(role => role.id_user_group === value);
            handleDirectInputChange('group', name);
            handleDirectInputChange('id_group', id_user_group);
        } else {
            handleDirectInputChange(name, value);
        }
    };

    const handleChangeDate = (e, name) => handleDirectInputChange(name, moment(e).format('YYYY/MM/DD'));

    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth disabled size="small" label={t('commonFields.documentId')} name="documentId"
                        value={dto?.documentId || t('commonFields.defaultDocumentId')}
                        onChange={handleInputChange}
                    />
                </Grid>
        
                <Grid size={{ xs: 12, md: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker 
                            label={t('commonFields.expeditionDate')}
                            format="DD/MM/YYYY" 
                            maxDate={moment()}
                            value={dto.expedition_date ? moment(dto.expedition_date) : null }
                            onChange={(e) => handleChangeDate(e, 'expedition_date')}
                            slotProps={{
                                 textField: {
                                    id: 'expedition_date',
                                    name: 'expedition_date',
                                    size: "small",
                                    fullWidth: true,
                                    required: false,
                                    error: Boolean(errorInputs['expedition_date']?.isError),
                                    helperText: errorInputs['expedition_date']?.messageError,
                                    InputLabelProps: { shrink: true }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker 
                            label={t('commonFields.expirationDate')}
                            format="DD/MM/YYYY" 
                            maxDate={moment()}
                            value={dto.payment_due_date ? moment(dto.payment_due_date) : null }
                            onChange={(e) => handleChangeDate(e, 'payment_due_date')}
                            slotProps={{
                                 textField: {
                                    id: 'payment_due_date',
                                    name: 'payment_due_date',
                                    size: "small",
                                    fullWidth: true,
                                    required: false,
                                    error: Boolean(errorInputs['payment_due_date']?.isError),
                                    helperText: errorInputs['payment_due_date']?.messageError,
                                    InputLabelProps: { shrink: true }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.currency?.isError)}
                    >
                        <InputLabel>{t('commonFields.currency')}</InputLabel>
                        <Select label={t('commonFields.currency')} name="currency"
                            value={dto.currency}
                            onChange={handleInputChange}
                        >
                            <MenuItem value="COP">Pesos</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.group?.isError)}
                    >
                        <InputLabel>{t('commonFields.group')}</InputLabel>
                        <Select label={t('commonFields.group')} name="id_group"
                            value={dto.id_group}
                            onChange={handleChange}
                            MenuProps={{ PaperProps: { style: { maxHeight: 200, overflowY: "auto" }}}}
                        >
                            {roles.map((item) => <MenuItem key={item.id_user_group} value={item.id_user_group}>{item.name}</MenuItem>)}
                        </Select>
                        {errorInputs.group?.isError && <FormHelperText>{errorInputs.group.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.approver?.isError)}
                    >
                        <InputLabel>{t('commonFields.approver')}</InputLabel>
                        <Select label={t('commonFields.approver')} name="id_approver"
                            value={dto.id_approver}
                            onChange={handleChange}
                            MenuProps={{ PaperProps: { style: { maxHeight: 200, overflowY: "auto" }}}}
                        >
                            {users.map((item) => <MenuItem key={item.id_user} value={item.id_user}>{item.user_name}</MenuItem>)}
                        </Select>
                        {errorInputs.approver?.isError && <FormHelperText>{errorInputs.approver.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.id_warehouse?.isError)}
                    >
                        <InputLabel>{t('commonFields.warehouse')}</InputLabel>
                        <Select label={t('commonFields.warehouse')} name="id_warehouse"
                            value={dto.id_warehouse}
                            onChange={handleChange}
                            MenuProps={{PaperProps: {style: { maxHeight: 200, overflowY: "auto" }}}}
                        >
                            {wareHouses.map((item) => <MenuItem key={item.code} value={item.code}>{item.name}</MenuItem>)}
                        </Select>
                        {errorInputs.id_warehouse?.isError && <FormHelperText>{errorInputs.id_warehouse.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>
            </Grid>
        </Box>
    );
});
